/**
 * Operators list page (refreshed).
 *
 * Visibility (server-enforced via ``operator_visibility_filter``):
 *
 * - SUPER operators see every row.
 * - TENANT operators see rows for operators with whom they share at
 *   least one ``AdminUserTenant`` grant.
 *
 * "Invite operator" button is super-only (UX hidden + server-side 403).
 */
import { useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Plus, RotateCw, ShieldCheck, User, Users } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { ListPagination } from "@/components/shell/ListPagination";
import { isMissingPage, LIST_PAGE_SIZE, usePageParam } from "@/lib/use-page-param";
import { RelativeTime } from "@/components/shell/RelativeTime";
import { DataTable, type DataTableColumn } from "@/components/shell/DataTable";
import { EmptyState } from "@/components/shell/EmptyState";
import { FilterChip } from "@/components/shell/FilterChip";
import { PageHeader } from "@/components/shell/PageHeader";
import { Toolbar, FilterGroup } from "@/components/shell/Toolbar";
import { getOperators, type AdminRole, type OperatorRow } from "@/lib/api";
import { useAuth } from "@/lib/use-auth";

const ROLES: readonly AdminRole[] = ["super", "tenant"] as const;
const ACTIVE_STATES = ["true", "false"] as const;

function operatorInitials(email: string, displayName: string): string {
  if (displayName) {
    const parts = displayName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (local.slice(0, 2) || "??").toUpperCase();
}

export function OperatorsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const { page, setPage } = usePageParam();

  const role = (params.get("role") as AdminRole | null) ?? undefined;
  const activeStr = params.get("is_active");
  const isActive: boolean | undefined =
    activeStr === "true" ? true : activeStr === "false" ? false : undefined;

  const query = useQuery({
    queryKey: ["admin-users", { role, isActive, page }] as const,
    queryFn: () => getOperators({ role, is_active: isActive, page, page_size: LIST_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.error && !isMissingPage(query.error, page)) {
      toast.error("Couldn't load operators", {
        description: "Check your network and try again.",
      });
    }
  }, [query.error, page]);

  function setFilter(key: "role" | "is_active", value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  const columns = useMemo<ReadonlyArray<DataTableColumn<OperatorRow>>>(
    () => [
      {
        key: "email",
        header: "Operator",
        cell: (r) => (
          <div className="flex min-w-0 items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary/10 text-[10px] text-primary">
                {operatorInitials(r.email, r.display_name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="truncate font-medium">{r.email}</div>
              {r.display_name ? (
                <div className="truncate text-xs text-muted-foreground">
                  {r.display_name}
                </div>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        key: "role",
        header: "Role",
        cell: (r) => (
          <Badge variant={r.role === "super" ? "default" : "muted"} className="capitalize">
            {r.role === "super" ? <ShieldCheck className="h-3 w-3" /> : <User className="h-3 w-3" />}
            {r.role}
          </Badge>
        ),
      },
      {
        key: "is_active",
        header: "Status",
        cell: (r) => (
          <Badge dot variant={r.is_active ? "success" : "muted"}>
            {r.is_active ? "active" : "inactive"}
          </Badge>
        ),
      },
      {
        key: "totp_enrolled",
        header: "TOTP",
        cell: (r) =>
          r.totp_enrolled ? (
            <Badge variant="success">enrolled</Badge>
          ) : (
            <span className="text-xs text-muted-foreground">not yet</span>
          ),
      },
      {
        key: "tenants",
        header: "Tenants",
        cell: (r) =>
          r.role === "super" ? (
            <span className="text-xs text-muted-foreground">all</span>
          ) : (
            <span className="font-mono text-xs tabular-nums">{r.tenants.length}</span>
          ),
      },
      {
        key: "last_login_at",
        header: "Last login",
        className: "text-muted-foreground",
        cell: (r) => (
          r.last_login_at ? <RelativeTime iso={r.last_login_at} /> : <span className="text-xs">—</span>
        ),
      },
    ],
    [],
  );

  const isSuper = user?.role === "super";

  return (
    <>
      <PageHeader
        icon={Users}
        title="Operators"
        description="Admin portal operators — super-admins and tenant-scoped operators."
        action={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
            >
              <RotateCw className={query.isFetching ? "animate-spin" : undefined} />
              Refresh
            </Button>
            {isSuper ? (
              <Button asChild size="sm">
                <Link to="/operators/new">
                  <Plus /> Invite operator
                </Link>
              </Button>
            ) : null}
          </>
        }
      />

      <Toolbar
        filters={
          <>
            <FilterGroup label="Role">
              <FilterChip active={role === undefined} onClick={() => setFilter("role", null)}>
                All
              </FilterChip>
              {ROLES.map((r) => (
                <FilterChip key={r} active={role === r} onClick={() => setFilter("role", r)}>
                  {r}
                </FilterChip>
              ))}
            </FilterGroup>
            <FilterGroup label="Status">
              <FilterChip
                active={isActive === undefined}
                onClick={() => setFilter("is_active", null)}
              >
                All
              </FilterChip>
              {ACTIVE_STATES.map((s) => (
                <FilterChip
                  key={s}
                  active={activeStr === s}
                  onClick={() => setFilter("is_active", s)}
                >
                  {s === "true" ? "active" : "inactive"}
                </FilterChip>
              ))}
            </FilterGroup>
          </>
        }
      />

      <DataTable<OperatorRow>
        stale={query.isPlaceholderData}
        columns={columns}
        rows={query.data?.results ?? []}
        loading={query.isLoading}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/operators/${r.id}`)}
        emptyState={
          <EmptyState
            icon={Users}
            title="No operators match these filters"
            description={
              isSuper
                ? "Clear a filter to see more rows, or invite a new operator."
                : "You only see operators sharing a tenant with you."
            }
            action={
              isSuper ? (
                <Button asChild size="sm">
                  <Link to="/operators/new">
                    <Plus /> Invite operator
                  </Link>
                </Button>
              ) : undefined
            }
          />
        }
      />

      <ListPagination query={query} page={page} onPage={setPage} />
    </>
  );
}

export default OperatorsListPage;
